import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import _ from "lodash";
import { BehaviorSubject, ReplaySubject, Subscription } from "rxjs";
import { Labels } from "src/app/labels/englishLabels";
import { RuleStatus, Severity, ViolationRule, ViolationRuleUpdateApiModel } from "src/app/models/api/governance/violationRule.model";
import { SeveritySelectOptions } from "src/app/models/selectOptions";
import { ViolationRulesHttpService } from "src/app/services/httpServices/governance/violationRulesHttpService/violation-rules-http.service";
import { ConfirmationDialogComponent } from "../confirmation-dialog/confirmation-dialog.component";
import { ISelectionButton } from "../selection-button/selection-button.component";

@Component({
    selector: "app-edit-violation-rule-dialog",
    templateUrl: "./edit-violation-rule-dialog.component.html",
    styleUrls: ["./edit-violation-rule-dialog.component.scss"],
})
export class EditViolationRuleDialogComponent implements OnInit, OnDestroy {
    public severity: Severity;
    public status: RuleStatus;
    public updateViolationRuleInProgress = false;

    public severitySelectionButtons = new BehaviorSubject<BehaviorSubject<ISelectionButton>[]>([]);

    private _violationRulesSubscription$?: Subscription;

    constructor(
        public dialogRef: MatDialogRef<EditViolationRuleDialogComponent>,
        @Inject(MAT_DIALOG_DATA) public violationRuleData: ViolationRule,
        private _violationRulesHttpService: ViolationRulesHttpService,
        private _matDialog: MatDialog,
    ) {
        this.dialogRef.disableClose = true;
        this.dialogRef.backdropClick().subscribe(() => {
            this.closeClick(false);
        });
    }

    public get RuleStatus(): typeof RuleStatus {
        return RuleStatus;
    }

    ngOnInit(): void {
        this.severity = _.cloneDeep(this.violationRuleData.severity);
        this.status = _.cloneDeep(this.violationRuleData.status);
        this.severitySelectionButtons.next(this._createSeveritySelectionButtons());
    }

    ngOnDestroy(): void {
        this._violationRulesSubscription$?.unsubscribe();
    }

    public saveChanges() {
        this._confirmationDialogSingleViolationRuleStatusChanged();
    }

    public onSeveritySelected(selectedButton: ISelectionButton) {
        this.severity = <Severity>selectedButton.text;
    }

    public onStatusSelected(value: boolean) {
        if (value) {
            this.status = RuleStatus.on;
        } else this.status = RuleStatus.off;
    }

    public closeClick(result = false): void {
        this.dialogRef.close(result);
    }

    private _createSeveritySelectionButtons() {
        const selectedIndex = SeveritySelectOptions.map((env) => env.value).indexOf(this.severity);
        const severitySelectionButtons: BehaviorSubject<ISelectionButton>[] = [];
        SeveritySelectOptions.forEach((severityOption, index) => {
            severitySelectionButtons.push(
                new BehaviorSubject<ISelectionButton>({
                    id: severityOption.id,
                    text: severityOption.displayName,
                    selected: index === selectedIndex ? true : false,
                }),
            );
        });
        return severitySelectionButtons;
    }

    private _confirmationDialogSingleViolationRuleStatusChanged(): ReplaySubject<boolean> {
        const success = new ReplaySubject<boolean>(1);

        const approveUpdatesDialogRef = this._matDialog.open(ConfirmationDialogComponent, {
            data: {
                title: Labels.policyPage.policy.violationRules.dialogsLabels.statusChangedTitle,
                content: Labels.policyPage.policy.violationRules.dialogsLabels.statusChangedMessageContent + "<br>" + this.violationRuleData.id,
            },
            disableClose: false,
            panelClass: "sized-popup-dialog-container",
        });
        approveUpdatesDialogRef.afterClosed().subscribe((userConfirmed) => {
            if (userConfirmed) {
                const violationRuleUpdate = {
                    status: this.status,
                    severity: this.severity,
                } as ViolationRuleUpdateApiModel;

                this.updateViolationRuleInProgress = true;
                this._violationRulesHttpService
                    .updateViolationRule(
                        this.violationRuleData.integrationId,
                        this.violationRuleData.policyId,
                        this.violationRuleData.id,
                        violationRuleUpdate,
                    )
                    .subscribe(
                        () => {
                            success.next(true);
                            this.updateViolationRuleInProgress = false;
                            this.closeClick(true);
                        },
                        () => {
                            this.updateViolationRuleInProgress = false;
                            success.next(false);
                        },
                    );
            }
        });
        return success;
    }
}
